import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '../api';
import { fetchDefaultGrouping } from '../utils/storeSettings';

interface ProductFinanceRow {
  'Товар': string;
  'Кол-во': number;
  'Выручка': number;
  'Ср.цена': number;
  'Расходы': number;
  'Налог': number;
  'Ср.расх': number | null;
  'Себес.': number;
  'ДоляОР': number;
  'Закупка': number;
  'Реклама': number;
  'Итог.рас': number;
  'Маржа': number;
  'Маржа,%': number | null;
}

interface ProductFinanceResponse {
  items: ProductFinanceRow[];
}

interface CachedProductFinance {
  data: ProductFinanceResponse;
  cachedAt: number;
}

type NumericValue = number | null | undefined;

type ColumnKey = keyof ProductFinanceRow | '№';

interface ColumnConfig {
  key: ColumnKey;
  label: string;
  align?: 'left' | 'right' | 'center';
  aggregate?: 'sum' | 'avg' | 'none';
  format?: (value: NumericValue | string) => string;
}

function truncateName(name: string, maxLength = 35): string {
  if (name.length <= maxLength) return name;
  return `${name.slice(0, maxLength)}…`;
}

function formatNumeric(value: NumericValue | string, digits?: number): string {
  if (value === null || value === undefined || typeof value === 'string') return '—';
  const hasFraction = Math.abs(value % 1) > 0;
  const fractionDigits = digits ?? (hasFraction ? 2 : 0);
  return value.toLocaleString('ru-RU', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

function formatInteger(value: NumericValue | string): string {
  return formatNumeric(value, 0);
}

function formatCurrency(value: NumericValue | string): string {
  return formatNumeric(value);
}

function formatPercent(value: NumericValue | string): string {
  if (value === null || value === undefined || typeof value === 'string') return '—';
  const formatted = formatNumeric(value);
  return `${formatted}%`;
}

function profitColor(value: NumericValue) {
  if (value === null || value === undefined) return 'text-slate-600';
  if (value > 0) return 'text-emerald-600';
  if (value < 0) return 'text-red-600';
  return 'text-slate-600';
}

function marginColor(value: NumericValue) {
  if (value === null || value === undefined) return 'text-slate-500';
  if (value >= 200) return 'text-emerald-600';
  if (value >= 100) return 'text-blue-600';
  if (value > 0) return 'text-amber-600';
  return 'text-red-600';
}

function formatDatePart(value: number): string {
  return String(value).padStart(2, '0');
}

function getCurrentMonthValue(): string {
  const now = new Date();
  return `${now.getFullYear()}-${formatDatePart(now.getMonth() + 1)}`;
}

function getMonthBoundaries(monthValue: string) {
  const [yearStr, monthStr] = monthValue.split('-');
  const year = Number(yearStr);
  const month = Number(monthStr);
  const start = `${year}-${formatDatePart(month)}-01`;
  const nextMonth = month === 12 ? 1 : month + 1;
  const nextYear = month === 12 ? year + 1 : year;
  const end = `${nextYear}-${formatDatePart(nextMonth)}-01`;
  return { start, end };
}

function getMonthOptions(count = 12) {
  const now = new Date();
  return Array.from({ length: count }, (_, index) => {
    const date = new Date(now.getFullYear(), now.getMonth() - index, 1);
    const value = `${date.getFullYear()}-${formatDatePart(date.getMonth() + 1)}`;
    const label = date.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
    return { value, label };
  });
}

const ProductFinanceTable: React.FC = () => {
  const initialMonth = getCurrentMonthValue();
  const initialBoundaries = getMonthBoundaries(initialMonth);

  const CACHE_TTL_MS = 10 * 60 * 1000;
  const [data, setData] = useState<ProductFinanceResponse>({ items: [] });
  const [dataCache, setDataCache] = useState<Record<string, CachedProductFinance>>({});
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState<string>(initialBoundaries.start);
  const [endDate, setEndDate] = useState<string>(initialBoundaries.end);
  const [selectedMonth, setSelectedMonth] = useState<string>(initialMonth);
  const [isRangeModalOpen, setIsRangeModalOpen] = useState(false);
  const [rangeStart, setRangeStart] = useState(initialBoundaries.start);
  const [rangeEnd, setRangeEnd] = useState(initialBoundaries.end);
  const [selectedProductName, setSelectedProductName] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const [isCardsCollapsed, setIsCardsCollapsed] = useState(false);
  const collapseStorageKey = 'product_finance_cards_collapsed';
  const [groupMode, setGroupMode] = useState<'products' | 'groups'>('groups');
  const [sortOption, setSortOption] = useState<'profit_desc' | 'profit_asc' | 'name_asc'>('profit_desc');
  const userChangedGroup = useRef(false);

  const handleGroupModeChange = (mode: 'products' | 'groups') => {
    userChangedGroup.current = true;
    setGroupMode(mode);
  };

  useEffect(() => {
    let isActive = true;
    fetchDefaultGrouping().then(mode => {
      if (!isActive || !mode || userChangedGroup.current) return;
      setGroupMode(mode);
    });
    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = window.localStorage.getItem(collapseStorageKey);
    if (stored === '1') setIsCardsCollapsed(true);
    if (stored === '0') setIsCardsCollapsed(false);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(collapseStorageKey, isCardsCollapsed ? '1' : '0');
  }, [isCardsCollapsed]);

  const columns: ColumnConfig[] = useMemo(
    () => [
      { key: '№', label: '№', align: 'center', aggregate: 'none' },
      {
        key: 'Товар',
        label: groupMode === 'groups' ? 'Категория' : 'Товар',
        align: 'left',
        aggregate: 'none',
        format: value => String(value),
      },
      { key: 'Маржа', label: 'Прибыль', align: 'right', aggregate: 'sum', format: formatCurrency },
      { key: 'Маржа,%', label: 'Маржа,%', align: 'right', aggregate: 'avg', format: formatPercent },
      { key: 'Кол-во', label: 'Кол-во', align: 'right', aggregate: 'sum', format: formatInteger },
      { key: 'Ср.цена', label: 'Ср.цена', align: 'right', aggregate: 'sum', format: formatCurrency },
      { key: 'Выручка', label: 'Выручка', align: 'right', aggregate: 'sum', format: formatCurrency },
      { key: 'Расходы', label: 'Расходы', align: 'right', aggregate: 'sum', format: formatCurrency },
      { key: 'Налог', label: 'Налог', align: 'right', aggregate: 'sum', format: formatCurrency },
      { key: 'Ср.расх', label: 'Ср.расх', align: 'right', aggregate: 'avg', format: formatCurrency },
      { key: 'ДоляОР', label: 'ДоляОР', align: 'right', aggregate: 'sum', format: formatCurrency },
      { key: 'Себес.', label: 'Себес.', align: 'right', aggregate: 'sum', format: formatCurrency },
      { key: 'Закупка', label: 'Закупка', align: 'right', aggregate: 'sum', format: formatCurrency },
      { key: 'Итог.рас', label: 'Итог.рас', align: 'right', aggregate: 'sum', format: formatCurrency },
    ],
    [groupMode],
  );

  useEffect(() => {
    async function load() {
      const cacheKey = `${startDate}|${endDate}|${groupMode}`;
      const cached = dataCache[cacheKey];
      if (cached && Date.now() - cached.cachedAt < CACHE_TTL_MS) {
        setData(cached.data);
        setLoading(false);
        return;
      }
      setLoading(true);
      const params = new URLSearchParams();
      if (startDate) params.set('start_date', startDate);
      if (endDate) params.set('end_date', endDate);
      if (groupMode === 'groups') params.set('view', 'groups');
      const res = await apiFetch(`/api/dashboard/product-finance?${params.toString()}`);
      if (res.ok) {
        const json: ProductFinanceResponse = await res.json();
        setData(json);
        setDataCache(prev => ({ ...prev, [cacheKey]: { data: json, cachedAt: Date.now() } }));
      }
      setLoading(false);
    }
    load().catch(console.error);
  }, [startDate, endDate, groupMode, dataCache]);

  const totals = useMemo(() => {
    if (!data.items.length) return null;

    const summary: Record<string, number | string> = { Товар: 'Итого' };

    columns.forEach((column) => {
      if (column.aggregate === 'none') return;
      const values = data.items
        .map((row) => (row as unknown as Record<string, unknown>)[column.key])
        .filter((v): v is number => typeof v === 'number' && !Number.isNaN(v));

      if (!values.length) {
        summary[column.key] = 0;
        return;
      }

      if (column.aggregate === 'avg') {
        summary[column.key] = values.reduce((acc, v) => acc + v, 0) / values.length;
      } else {
        summary[column.key] = values.reduce((acc, v) => acc + v, 0);
      }
    });

    return summary;
  }, [columns, data.items]);

  const sortedItems = useMemo(() => {
    const items = [...data.items];

    switch (sortOption) {
      case 'profit_asc':
        return items.sort((a, b) => (a['Маржа'] ?? 0) - (b['Маржа'] ?? 0));
      case 'name_asc':
        return items.sort((a, b) => a['Товар'].localeCompare(b['Товар']));
      case 'profit_desc':
      default:
        return items.sort((a, b) => (b['Маржа'] ?? 0) - (a['Маржа'] ?? 0));
    }
  }, [data.items, sortOption]);

  useEffect(() => {
    if (viewMode !== 'cards') return undefined;

    const cards = Array.from(document.querySelectorAll<HTMLDivElement>('.product-card'));

    const getCols = () => {
      if (window.innerWidth >= 1280) return 3;
      if (window.innerWidth >= 768) return 2;
      return 1;
    };

    const cols = getCols();
    const cleanups: Array<() => void> = [];

    cards.forEach((card, index) => {
      const details = card.querySelector<HTMLDetailsElement>('details');
      if (!details) return;

      const toggleHandler = () => {
        if (window.innerWidth < 768) return;

        const isOpen = details.open;
        const row = Math.floor(index / cols);

        cards.forEach((card2, idx2) => {
          const details2 = card2.querySelector<HTMLDetailsElement>('details');
          if (!details2) return;

          const row2 = Math.floor(idx2 / cols);
          if (row2 === row && details2.open !== isOpen) {
            details2.open = isOpen;
          }
        });
      };

      details.addEventListener('toggle', toggleHandler);
      cleanups.push(() => details.removeEventListener('toggle', toggleHandler));
    });

    return () => {
      cleanups.forEach(unsub => unsub());
    };
  }, [sortedItems, viewMode]);

  const monthOptions = useMemo(() => getMonthOptions(), []);

  const openRangeModal = () => {
    setRangeStart(startDate);
    setRangeEnd(endDate);
    setIsRangeModalOpen(true);
  };

  const handleMonthChange = (value: string) => {
    const { start, end } = getMonthBoundaries(value);
    setSelectedMonth(value);
    setStartDate(start);
    setEndDate(end);
  };

  const applyRange = () => {
    setStartDate(rangeStart);
    setEndDate(rangeEnd);
    setIsRangeModalOpen(false);
  };

  const getStickyHeaderClasses = (_column: ColumnConfig) => '';

  const getStickyCellClasses = (_column: ColumnConfig) => '';

  const getWidthClasses = (column: ColumnConfig) => {
    if (column.key === '№') return 'w-6 min-w-[1.5rem]';
    if (column.key === 'Товар') return 'min-w-[14rem]';
    return '';
  };

  const getPaddingClasses = (column: ColumnConfig) => {
    if (column.key === '№') return 'py-2 px-1';
    return 'py-2 pr-4';
  };

  const fillRangeFromMonth = (value: string) => {
    const { start, end } = getMonthBoundaries(value);
    setRangeStart(start);
    setRangeEnd(end);
    setSelectedMonth(value);
  };

  const monthBoundaries = useMemo(
    () => getMonthBoundaries(selectedMonth),
    [selectedMonth],
  );

  const isCustomRange = useMemo(
    () => startDate !== monthBoundaries.start || endDate !== monthBoundaries.end,
    [endDate, monthBoundaries.end, monthBoundaries.start, startDate],
  );

  if (loading && !data.items.length) {
    return (
      <div className="bg-white rounded-2xl shadow p-4 mb-8">Загрузка фин. данных...</div>
    );
  }

  if (!data.items.length) return null;

  return (
    <div className="max-w-7xl mx-auto mt-8 w-full">
      <div className="bg-white rounded-2xl shadow overflow-hidden">
        <div className="flex flex-col gap-3 px-6 py-5 border-b md:flex-row md:items-center md:justify-between">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <h2 className="text-2xl font-bold text-gray-800">
              Финансы по {groupMode === 'groups' ? 'категориям' : 'товарам'}
            </h2>
            <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
              <button
                type="button"
                className={`px-3 py-1 text-sm font-medium rounded-lg transition ${
                  groupMode === 'products'
                    ? 'bg-indigo-600 text-white shadow'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
                onClick={() => handleGroupModeChange('products')}
              >
                по товарам
              </button>
              <button
                type="button"
                className={`px-3 py-1 text-sm font-medium rounded-lg transition ${
                  groupMode === 'groups'
                    ? 'bg-indigo-600 text-white shadow'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
                onClick={() => handleGroupModeChange('groups')}
              >
                по категориям
              </button>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 justify-end">
            <label className="text-sm text-gray-600" htmlFor="product-finance-month">
              Месяц:
            </label>
            <select
              id="product-finance-month"
              className="border rounded-lg px-3 py-2 text-sm shadow-sm"
              value={selectedMonth}
              onChange={e => handleMonthChange(e.target.value)}
            >
              {monthOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={openRangeModal}
              className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm border bg-white shadow-sm transition ${
                isCustomRange
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-slate-200 text-slate-700 hover:border-indigo-200 hover:text-indigo-600'
              } whitespace-nowrap`}
              title="Выбрать даты"
            >
              <span role="img" aria-label="calendar">📅</span>
              {isCustomRange && <span className="text-xs font-medium">Диапазон</span>}
            </button>
            <div className="flex items-center gap-1 bg-slate-100 rounded-full p-1 shadow-inner">
              <button
                type="button"
                onClick={() => setViewMode('cards')}
                className={`p-2 rounded-full transition ${
                  viewMode === 'cards'
                    ? 'bg-indigo-600 text-white shadow'
                    : 'text-slate-700 hover:bg-white'
                }`}
                aria-label="Показать карточки"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  className="w-5 h-5"
                  aria-hidden="true"
                >
                  <rect x="4" y="4" width="7" height="7" rx="1.5" />
                  <rect x="13" y="4" width="7" height="7" rx="1.5" />
                  <rect x="4" y="13" width="7" height="7" rx="1.5" />
                  <rect x="13" y="13" width="7" height="7" rx="1.5" />
                </svg>
                <span className="sr-only">Карточки</span>
              </button>
              <button
                type="button"
                onClick={() => setViewMode('table')}
                className={`p-2 rounded-full transition ${
                  viewMode === 'table'
                    ? 'bg-indigo-600 text-white shadow'
                    : 'text-slate-700 hover:bg-white'
                }`}
                aria-label="Показать таблицу"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  className="w-5 h-5"
                  aria-hidden="true"
                >
                  <rect x="3.5" y="5" width="17" height="14" rx="2" />
                  <line x1="3.5" y1="10" x2="20.5" y2="10" />
                  <line x1="9" y1="5" x2="9" y2="19" />
                  <line x1="15" y1="5" x2="15" y2="19" />
                </svg>
                <span className="sr-only">Таблица</span>
              </button>
            </div>
            <label className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2 shadow-sm">
              <span className="text-sm text-slate-500">Сортировка</span>
              <select
                className="text-sm font-medium outline-none bg-transparent"
                value={sortOption}
                onChange={e => setSortOption(e.target.value as typeof sortOption)}
              >
                <option value="profit_desc">по прибыли ↓</option>
                <option value="profit_asc">по прибыли ↑</option>
                <option value="name_asc">по названию A–Я</option>
              </select>
            </label>
          </div>
        </div>

        <div className="p-6 bg-slate-50 space-y-6">
          {totals && (
            <button
              type="button"
              onClick={() => setIsCardsCollapsed(prev => !prev)}
              className="w-full text-left bg-indigo-50 border border-indigo-100 rounded-2xl p-4 shadow-sm hover:shadow-md transition"
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <div className="text-xs uppercase tracking-wide text-indigo-600 font-semibold">
                    Итого по всем {groupMode === 'groups' ? 'категориям' : 'товарам'}
                  </div>
                  <div className="mt-1 text-xl font-bold text-slate-900">
                    Прибыль:{' '}
                    <span className={profitColor(totals['Маржа'] as NumericValue)}>
                      {formatCurrency(totals['Маржа'] as NumericValue)} ₽
                    </span>
                  </div>
                  <div className="text-sm text-slate-600">
                    Маржа:{' '}
                    <span className={`${marginColor(totals['Маржа,%'] as NumericValue)} font-semibold`}>
                      {formatPercent(totals['Маржа,%'] as NumericValue)}
                    </span>
                    , Кол-во: <span className="font-semibold">{formatInteger(totals['Кол-во'] as NumericValue)}</span>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2 text-xs md:text-sm">
                  <div className="bg-white rounded-xl px-3 py-2 shadow-sm">
                    <div className="text-slate-500">Выручка</div>
                    <div className="font-semibold text-blue-600">{formatCurrency(totals['Выручка'] as NumericValue)}</div>
                  </div>
                  <div className="bg-white rounded-xl px-3 py-2 shadow-sm">
                    <div className="text-slate-500">Расходы</div>
                    <div className="font-semibold text-red-600">{formatCurrency(totals['Расходы'] as NumericValue)}</div>
                  </div>
                  <div className="bg-white rounded-xl px-3 py-2 shadow-sm">
                    <div className="text-slate-500">Налог</div>
                    <div className="font-semibold text-amber-600">{formatCurrency(totals['Налог'] as NumericValue)}</div>
                  </div>
                </div>
              </div>
            </button>
          )}

          {viewMode === 'cards' ? (
            !isCardsCollapsed && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {sortedItems.map((row, index) => (
                <div
                  key={`${row['Товар']}-${index}`}
                  className="product-card bg-white rounded-2xl border border-slate-200 p-4 shadow-sm hover:shadow-md transition"
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center text-slate-50 text-sm font-semibold shrink-0">
                      {index + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-slate-900 truncate whitespace-nowrap" title={row['Товар']}>
                        {row['Товар']}
                      </h3>
                      <div className={`mt-1 text-lg font-bold ${profitColor(row['Маржа'])}`}>
                        {(row['Маржа'] ?? 0) > 0 ? '+' : ''}
                        {formatCurrency(row['Маржа'])} ₽
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-center text-xs mt-1">
                    <div className="p-2 bg-slate-50 rounded-lg">
                      <div className="text-slate-500">Маржа</div>
                      <div className={`mt-1 text-sm font-semibold ${marginColor(row['Маржа,%'])}`}>
                        {formatPercent(row['Маржа,%'])}
                      </div>
                    </div>
                    <div className="p-2 bg-slate-50 rounded-lg">
                      <div className="text-slate-500">Кол-во</div>
                      <div className="mt-1 text-sm font-semibold">{formatInteger(row['Кол-во'])}</div>
                    </div>
                    <div className="p-2 bg-slate-50 rounded-lg">
                      <div className="text-slate-500">Ср. цена</div>
                      <div className="mt-1 text-sm font-semibold">
                        {formatCurrency(row['Ср.цена'])}
                      </div>
                    </div>
                    <div className="p-2 bg-slate-50 rounded-lg">
                      <div className="text-slate-500">Выручка</div>
                      <div className="mt-1 text-sm font-semibold text-blue-600">
                        {formatCurrency(row['Выручка'])}
                      </div>
                    </div>
                    <div className="p-2 bg-slate-50 rounded-lg">
                      <div className="text-slate-500">Расходы</div>
                      <div className="mt-1 text-sm font-semibold text-red-600">
                        {formatCurrency(row['Расходы'])}
                      </div>
                    </div>
                    <div className="p-2 bg-slate-50 rounded-lg">
                      <div className="text-slate-500">Налог</div>
                      <div className="mt-1 text-sm font-semibold text-amber-600">
                        {formatCurrency(row['Налог'])}
                      </div>
                    </div>
                  </div>

                  <details className="group mt-3 text-sm">
                    <summary className="cursor-pointer select-none text-slate-600 flex items-center justify-between">
                      <span>Подробнее</span>
                      <span className="chevron text-xs text-slate-400 ml-2 transition-transform duration-150 group-open:rotate-180">▼</span>
                    </summary>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <div className="p-2 bg-slate-50 rounded">
                        <div className="text-xs text-slate-500">Ср. расход</div>
                        <div className="font-semibold">{formatCurrency(row['Ср.расх'])}</div>
                      </div>
                      <div className="p-2 bg-slate-50 rounded">
                        <div className="text-xs text-slate-500">Доля ОР</div>
                        <div className="font-semibold">{formatCurrency(row['ДоляОР'])}</div>
                      </div>
                      <div className="p-2 bg-slate-50 rounded">
                        <div className="text-xs text-slate-500">Себестоимость</div>
                        <div className="font-semibold">{formatCurrency(row['Себес.'])}</div>
                      </div>
                      <div className="p-2 bg-slate-50 rounded">
                        <div className="text-xs text-slate-500">Закупка</div>
                        <div className="font-semibold">{formatCurrency(row['Закупка'])}</div>
                      </div>
                      <div className="p-2 bg-slate-50 rounded">
                        <div className="text-xs text-slate-500">Реклама</div>
                        <div className="font-semibold">{formatCurrency(row['Реклама'])}</div>
                      </div>
                      <div className="p-2 bg-slate-50 rounded">
                        <div className="text-xs text-slate-500">Итог. расход</div>
                        <div className="font-semibold">{formatCurrency(row['Итог.рас'])}</div>
                      </div>
                    </div>
                  </details>
                </div>
              ))}
            </div>
            )
          ) : (
            <div className="overflow-auto max-h-[70vh] rounded-xl border border-slate-200">
              <table className="min-w-full text-xs sm:text-sm bg-white">
                <thead className="bg-slate-50 text-slate-600 sticky top-0 z-20">
                  <tr>
                    {columns.map((column) => (
                      <th
                        key={column.key as string}
                        className={`${getPaddingClasses(column)} ${
                          column.align === 'right'
                            ? 'text-right'
                            : column.align === 'center'
                              ? 'text-center'
                              : 'text-left'
                        } sticky top-0 z-20 bg-slate-50 ${getStickyHeaderClasses(column)} ${getWidthClasses(column)}`}
                      >
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {sortedItems.map((row, index) => (
                    <tr
                      key={`${row['Товар']}-${index}`}
                      className="hover:bg-slate-50 cursor-pointer"
                      onClick={() => setSelectedProductName(row['Товар'])}
                    >
                      {columns.map((column) => (
                        <td
                          key={column.key as string}
                          className={`${getPaddingClasses(column)} ${
                            column.align === 'right'
                              ? 'text-right'
                              : column.align === 'center'
                                ? 'text-center'
                                : 'text-left'
                          } ${getStickyCellClasses(column)} ${getWidthClasses(column)}`}
                          title={column.key === 'Товар' ? row['Товар'] : undefined}
                        >
                          {column.key === '№'
                            ? index + 1
                            : column.key === 'Товар'
                              ? truncateName(String(row['Товар']))
                              : column.format
                                ? column.format(row[column.key as keyof ProductFinanceRow])
                                : String(row[column.key as keyof ProductFinanceRow] ?? '—')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
                {totals && (
                  <tfoot>
                    <tr className="bg-slate-50 font-semibold text-slate-800">
                      {columns.map((column) => (
                        <td
                          key={column.key as string}
                          className={`${getPaddingClasses(column)} ${
                            column.align === 'right'
                              ? 'text-right'
                              : column.align === 'center'
                                ? 'text-center'
                                : 'text-left'
                          } ${getStickyCellClasses(column)} ${getWidthClasses(column)}`}
                        >
                          {column.format && column.aggregate !== 'none'
                            ? column.format(totals[column.key] as NumericValue)
                            : column.aggregate === 'none'
                              ? column.key === '№'
                                ? ''
                                : String(totals[column.key] ?? 'Итого')
                              : String(totals[column.key] ?? '—')}
                        </td>
                      ))}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}
        </div>
      </div>

      {isRangeModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Выбор дат</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600" htmlFor="product-finance-month-modal">
                  Месяц:
                </label>
                <select
                  id="product-finance-month-modal"
                  className="border rounded px-2 py-1 text-sm flex-1"
                  value={selectedMonth}
                  onChange={e => fillRangeFromMonth(e.target.value)}
                >
                  {monthOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                  <label className="text-sm text-gray-600" htmlFor="product-finance-start">
                    С
                  </label>
                  <input
                    id="product-finance-start"
                    type="date"
                    className="border rounded px-2 py-1 text-sm"
                    value={rangeStart}
                    onChange={e => setRangeStart(e.target.value)}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm text-gray-600" htmlFor="product-finance-end">
                    По
                  </label>
                  <input
                    id="product-finance-end"
                    type="date"
                    className="border rounded px-2 py-1 text-sm"
                    value={rangeEnd}
                    onChange={e => setRangeEnd(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  className="px-3 py-1 rounded border text-sm text-gray-700 hover:bg-gray-50"
                  onClick={() => setIsRangeModalOpen(false)}
                >
                  Отмена
                </button>
                <button
                  type="button"
                  className="px-3 py-1 rounded bg-indigo-600 text-white text-sm hover:bg-indigo-700"
                  onClick={applyRange}
                >
                  Применить
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedProductName && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Полное наименование {groupMode === 'groups' ? 'категории' : 'товара'}
            </h3>
            <p className="text-gray-700 break-words mb-4">{selectedProductName}</p>
            <div className="flex justify-end">
              <button
                type="button"
                className="px-3 py-1 rounded bg-indigo-600 text-white text-sm hover:bg-indigo-700"
                onClick={() => setSelectedProductName(null)}
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductFinanceTable;
